import React from 'react';
import {useMergeRefs} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';

import {ModalContent} from '../ModalContent';
import {ModalCard} from '../ModalCard';
import {ModalDraggableWrapper} from '../ModalDraggableWrapper';

import type {ModalCardProps} from '../ModalCard';
import type {ModalContentProps} from '../ModalContent';
import type {ModalDraggableProps} from '../ModalDraggableWrapper';

export type ModalDialogProps = ModalCardProps &
  ModalContentProps & {
    /**
     * When true, the Modal can be dragged to reposition it.
     */
    isDraggable?: boolean;
    /**
     * Pass-through props for react-draggable (e.g. `bounds`, `axis`).
     * Dragging is always restricted to the ModalHeader.
     */
    draggableProps?: ModalDraggableProps;
    /**
     * When true, prevents dragging even when isDraggable is true.
     */
    isDragDisabled?: boolean;
  };

export const ModalDialog = React.forwardRef<HTMLDivElement, ModalDialogProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      children,
      draggableProps,
      elevation = 3,
      hasSoftCorners = false,
      height,
      isCentered = false,
      isDraggable,
      isDragDisabled,
      isFullHeight,
      isFullScreen,
      _margin,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      modalSize = 'medium',
      scrollBehavior = 'inside',
      shouldDisableAnimations = false,
      shouldPreventBodyScroll = true,
      shouldUseBounceAnimation = true,
      width,
      style = emptyObject,
      ...props
    },
    ref
  ) => {
    const modalRef = React.useRef<HTMLDivElement>(null);
    const mergedRefs = useMergeRefs([modalRef, ref]);

    const effectiveScrollBehavior = isFullScreen
      ? ('inside' as const)
      : scrollBehavior;

    const dialogContentProps = {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      isCentered,
      isFullHeight,
      isFullScreen,
      _margin,
      shouldDisableAnimations,
      shouldPreventBodyScroll,
      shouldUseBounceAnimation,
    };
    const cardProps = {
      elevation,
      hasSoftCorners,
      height,
      isCentered,
      isFullHeight,
      isFullScreen,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      modalSize,
      scrollBehavior: effectiveScrollBehavior,
      style,
      width,
      ...props,
    };

    const card = (
      <ModalCard
        {...cardProps}
        ref={mergedRefs}
        shouldRenderDragHandles={
          isDraggable && isDragDisabled !== true && !isFullScreen
        }
      >
        {children}
      </ModalCard>
    );

    return (
      <ModalContent {...dialogContentProps}>
        {isDraggable ? (
          <ModalDraggableWrapper
            {...draggableProps}
            isDragDisabled={isDragDisabled}
            isFullScreen={isFullScreen}
            modalRef={modalRef}
          >
            {card}
          </ModalDraggableWrapper>
        ) : (
          card
        )}
      </ModalContent>
    );
  }
);

ModalDialog.displayName = 'ModalDialog';
